import type {
  HTMLAttributes,
  ReactNode,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from "react";

import { DefinedDefaults } from "@/ui/lib/types";
import type { TIconKey } from "@/ui/components/primitives/Icon";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const TABLE_CLASS_NAME =
  "w-full caption-top border-collapse font-sys-default text-sys-sm leading-sys-1-3 text-foreground";

export const TABLE_CAPTION_CLASS_NAME =
  "mb-2 text-left text-sys-sm leading-sys-1-3 text-foreground-muted-strong";

export const TABLE_CELL_CLASS_NAME = "px-3 py-2 align-top";

export const TABLE_HEADER_CELL_CLASS_NAME =
  "px-3 py-2 text-left align-top font-bold text-foreground";

export const TABLE_ROW_DIVIDER_CLASS_NAME = "border-b border-solid border-muted";

export const TABLE_ROW_INDENT_CLASS_NAME = "[&>*:first-child]:pl-6";

/*-------------------------------------
|  Types
-------------------------------------*/

export type TTableProps = Omit<TableHTMLAttributes<HTMLTableElement>, "caption"> & {
  caption: ReactNode;
  captionHidden?: boolean;
};

export type TTableSectionProps = HTMLAttributes<HTMLTableSectionElement>;

export type TTRProps = HTMLAttributes<HTMLTableRowElement> & {
  divider?: boolean;
  indent?: boolean;
};

export type TTHScope = "row" | "col";

type TTHBaseProps = Omit<ThHTMLAttributes<HTMLTableCellElement>, "scope"> & {
  icon?: TIconKey;
};

type TTHRowProps = TTHBaseProps & {
  scope: "row";
};

type TTHColProps = TTHBaseProps & {
  scope: "col";
};

export type TTHProps = TTHRowProps | TTHColProps;

export type TTDProps = TdHTMLAttributes<HTMLTableCellElement>;

type TTableDefaultProps = DefinedDefaults<TTableProps, "captionHidden">;

type TTRDefaultProps = DefinedDefaults<TTRProps, "divider" | "indent">;

/*-------------------------------------
|  Defaults
-------------------------------------*/

export const TABLE_DEFAULTS = {
  captionHidden: false,
} satisfies TTableDefaultProps;

export const TABLE_ROW_DEFAULTS = {
  divider: false,
  indent: false,
} satisfies TTRDefaultProps;
